/** The product editor's data in the admin panel, plus query keys and status lists of the admin screens. */

import {
  adminBrandsList,
  adminCategoriesFormSchemaRetrieve,
  adminCategoriesTree,
  adminTaxClassesList,
  type ApprovalStatusEnum,
  type TreeNode,
} from "@motolynk/api-client";

import type { CategoryNode, EditorSource } from "../editor/values";

export const productKeys = {
  all: ["admin", "products"] as const,
  list: (params: object) => ["admin", "products", "list", params] as const,
  counts: () => ["admin", "products", "counts"] as const,
  detail: (uid: string) => ["admin", "products", "detail", uid] as const,
  history: (uid: string) => ["admin", "products", "history", uid] as const,
};

export const STATUS_TABS = [
  "pending_review",
  "approved",
  "changes_requested",
  "rejected",
  "draft",
  "all",
] as const satisfies readonly (ApprovalStatusEnum | "all")[];

export type StatusTab = (typeof STATUS_TABS)[number];

function toNodes(nodes: TreeNode[]): CategoryNode[] {
  return nodes.map((node) => ({
    uid: node.uid,
    name: node.name,
    children: toNodes((node.children ?? []) as unknown as TreeNode[]),
  }));
}

/** Every page of a paged list (the API returns at most 100 rows at a time). */
async function allBrands(): Promise<{ uid: string; name: string }[]> {
  const out: { uid: string; name: string }[] = [];
  for (let page = 1; page < 50; page += 1) {
    const { data } = await adminBrandsList({ page, page_size: 100, active: true });
    out.push(...data.results.map((brand) => ({ uid: brand.uid, name: brand.name })));
    if (!data.next) break;
  }
  return out;
}

export const adminSource: EditorSource = {
  key: "admin",
  categories: async () => toNodes((await adminCategoriesTree()).data),
  brands: allBrands,
  taxClasses: async () =>
    (await adminTaxClassesList({ active: true })).data.map((tax) => ({
      uid: tax.uid,
      name: tax.name,
    })),
  formSchema: async (category) => (await adminCategoriesFormSchemaRetrieve(category)).data,
};
